// @flow
import { print } from 'graphql/language/printer';
import { CombinedError } from 'urql';
import { share, pipe, filter, make, mergeMap, takeUntil, merge, fromArray } from 'wonka';
import type { ExchangeInput, ExchangeIO, Operation, OperationResult } from 'urql';
import type { Talkback, Signal, Operator, Source } from 'wonka';

// A wonka operator (read: reactive pipeline transformer) that batches data based upon a timeout.
// Pending https://github.com/kitten/wonka/issues/26 we can remove this.
const batch = <T>(delay: number): Operator<T, Array<T>> => source => sink => {
  // The generated JS types for the wonka unions are pretty terrible.  They're tagged unions.  These are the tags.
  const signalTags = { start: 0, push: 1 };
  const talkbackTags = { pull: 0, close: 1 };

  const state: {
    // Each node in the pipeline can communicate with those ahead and behind it so that data may be pushed and/or
    // pulled and so that the node can initialize and deinitialize itself.  Talkback is how we communicate with our
    // forebears.
    talkback: ?Talkback,
    // The current batch.
    batch: ?Array<T>,
    // Whether we've been told to shut down.  This will happen when there is nobody listening for our output.
    gotEndSignal: boolean,
  } = {
    talkback: null,
    batch: null,
    gotEndSignal: false,
  };

  source(signal => {
    if (typeof signal === 'number') {
      // End!  Do nothing.
    } else if (signal.tag === signalTags.start) {
      const talkback = signal[0];
      state.talkback = talkback;
    } else if (signal.tag === signalTags.push) {
      const value = signal[0];
      if (state.batch) {
        state.batch.push(value);
      } else if (state.gotEndSignal) {
        sink(0);
      } else {
        state.batch = [value];
        setTimeout(() => {
          const batchedSignal: Signal<Array<T>> = ([state.batch]: any);
          // $FlowFixMe: Can't convince flow to accept any value as both a tuple and an object.
          batchedSignal.tag = signalTags.push;
          state.batch = null;
          sink(batchedSignal);
        }, delay);
      }
    }
  });

  const start: Signal<Array<T>> = ([
    talkback => {
      if (talkback === talkbackTags.close) {
        state.gotEndSignal = true;
        if (!state.batch) {
          sink(0);
        }
      } else if (!state.gotEndSignal) {
        // $FlowFixMe: Asserting non-null.
        state.talkback(talkback);
      }
    },
  ]: any);
  // $FlowFixMe: Can't convince flow to accept any value as both a tuple and an object.
  start.tag = signalTags.start;
  sink(start);
};

// Exported so we can test with mocked clocks.
export const batchMilliseconds = 100;

// An exchange that dispatches batched requests using fetch.  It's copied and adapted from the builtin fetch exchange.
// It's mostly identical except for changing the cardinality of requests (handling a list instead of a single request).
// https://github.com/FormidableLabs/urql/blob/823b81fcaf2e7ec3da10913c8d7b4a262d08f66a/src/exchanges/fetch.ts#L8
//
// (This is in an object just so I can order my functions in the same direction that control flows.)
const BatchFetchExchange = {
  create({ forward }: ExchangeInput): ExchangeIO {
    return ops$ => {
      const sharedOps$ = share(ops$);
      const fetchResults$ = pipe(
        sharedOps$,
        filter(op => {
          const { operationName } = op;
          return (
            operationName === 'query' ||
            operationName === 'mutation' ||
            operationName === 'teardown'
          );
        }),
        batch(batchMilliseconds),
        mergeMap(operations => {
          // First, filter out any operations that were torn down while waiting for the batch to fire.
          // Make sure the teardown happened _after_ the submission, otherwise we shouldn't discard the operation!
          let requestOperations = [];
          operations.forEach(op => {
            if (op.operationName === 'teardown') {
              requestOperations = requestOperations.filter(op2 => op2.key !== op.key);
            } else {
              requestOperations.push(op);
            }
          });

          // Now, actually fetch the remaining operations, if any.  Abort the fetch if we receive teardowns for all
          // remaining operations while the fetch is in-progress.
          if (requestOperations.length === 0) {
            return fromArray([]);
          }
          const requestKeys = new Set(requestOperations.map(o => o.key));
          const teardown$ = pipe(
            sharedOps$,
            // Emits only when all of the request in this batch have been cancelled.
            filter(
              op =>
                op.operationName === 'teardown' &&
                requestKeys.delete(op.key) &&
                requestKeys.size === 0,
            ),
          );

          return pipe(
            BatchFetchExchange.createBatchFetchSource(requestOperations),
            takeUntil(teardown$),
          );
        }),
      );

      const forward$ = pipe(
        sharedOps$,
        filter(op => {
          const { operationName } = op;
          return operationName !== 'query' && operationName !== 'mutation';
        }),
        forward,
      );

      return merge([fetchResults$, forward$]);
    };
  },

  createBatchFetchSource(operations: Array<Operation>): Source<OperationResult<any>> {
    if (operations.some(o => o.operationName === 'subscription')) {
      throw new Error(
        'Received a subscription operation in the httpExchange. You are probably trying to create a subscription. Have you added a subscriptionExchange?',
      );
    }

    return make(([next, complete]) => {
      const abortController = new AbortController();

      // This is the same thing that apollo-link-batch-http does.  It feels dumb to me but what do I know.  I feel like
      // batches should really be grouped by distinct fetchOptions.
      const { context } = operations[0];

      const extraOptions: RequestOptions =
        typeof context.fetchOptions === 'function'
          ? context.fetchOptions()
          : context.fetchOptions || {};

      const headers = new Headers(extraOptions.headers);
      headers.set('content-type', 'application/json');

      const fetchOptions = {
        body: JSON.stringify(
          operations.map(o => ({
            query: print(o.query),
            variables: o.variables,
          })),
        ),
        method: 'POST',
        ...extraOptions,
        headers,
        signal: abortController.signal,
      };

      BatchFetchExchange.executeBatchFetch(operations, fetchOptions).then(results => {
        results.forEach(result => {
          if (result !== undefined) {
            next(result);
          }
        });

        complete();
      });

      return () => {
        abortController.abort();
      };
    });
  },

  executeBatchFetch(
    operations: Array<Operation>,
    opts: RequestOptions,
  ): Promise<Array<OperationResult<any>>> {
    let response: ?Response;
    const { url } = operations[0].context;

    return fetch(url, { ...opts, credentials: 'same-origin' })
      .then(res => {
        response = res;
        BatchFetchExchange.checkStatus(opts.redirect, response);
        return response.json();
      })
      .then(results =>
        results.map((result, i) => ({
          operation: operations[i],
          data: result.data,
          error: Array.isArray(result.errors)
            ? new CombinedError({
                graphQLErrors: result.errors,
                response,
              })
            : undefined,
        })),
      )
      .catch(err => {
        if (err.name === 'AbortError') {
          return Array(operations.length).fill(undefined);
        }
        return operations.map(o => ({
          operation: o,
          data: undefined,
          error: new CombinedError({
            networkError: err,
            response,
          }),
        }));
      });
  },

  checkStatus(redirectMode: string = 'follow', response: Response) {
    const statusRangeEnd = redirectMode === 'manual' ? 400 : 300;

    if (response.status < 200 || response.status >= statusRangeEnd) {
      throw new Error(response.statusText);
    }
  },
};

export const batchFetchExchange = BatchFetchExchange.create;
